/**
 * NoteActionButtonsView - Displays share, edit, and delete action buttons for notes
 *
 * Renders a horizontal stack of share, edit, and delete buttons with appropriate
 * icon colors based on the current theme. Share appears first to promote
 * collaborative workflows.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { Pencil, Trash2, UsersIcon } from "lucide-react-native";
import React, { memo } from "react";
import { Pressable } from "@/components/atoms/Pressable";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";
import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

/**
 * Props for NoteActionButtonsView component
 */
interface NoteActionButtonsViewProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Number of users this note has been shared with */
  readonly shareCount: number;
  /** Handler for share button press */
  readonly onSharePress: () => void;
  /** Handler for edit button press */
  readonly onEditPress: () => void;
  /** Handler for delete button press */
  readonly onDeletePress: () => void;
  /** Whether to show the share button (hidden when org defaults to public) */
  readonly showShareButton?: boolean;
}

/** Icon color class (static — semantic tokens auto-invert by theme) */
const ICON_CLASS = "text-content-muted";

/** Pill button background class (static) */
const PILL_BG_CLASS = "bg-surface-raised";

/** Pill button text class (static) */
const PILL_FG_CLASS = "text-content-secondary";

/**
 * NoteActionButtonsView renders share, edit, and delete action buttons
 * @param props - View props
 * @param props.isDark - Whether dark mode is active
 * @param props.shareCount - Number of users this note has been shared with
 * @param props.onSharePress - Handler for share button press
 * @param props.onEditPress - Handler for edit button press
 * @param props.onDeletePress - Handler for delete button press
 * @param props.showShareButton - Whether to show the share button
 * @returns HStack containing share, edit, and delete buttons
 */
const NoteActionButtonsView = ({
  shareCount,
  onSharePress,
  onEditPress,
  onDeletePress,
  showShareButton,
}: NoteActionButtonsViewProps) => (
  <Stack direction="horizontal" className="items-center gap-3">
    {showShareButton !== false && (
      <Pressable onPress={onSharePress} hitSlop={8}>
        <Box
          className={`flex-row items-center gap-1 rounded-sm border border-outline-subtle px-3 py-1 ${PILL_BG_CLASS}`}
        >
          <Icon as={UsersIcon} size="xs" className={ICON_CLASS} />
          <Text variant="body" className={`font-medium ${PILL_FG_CLASS}`}>
            {shareCount > 0 ? "Shared" : "Share"}
          </Text>
          {shareCount > 0 && (
            <Box className="size-5 items-center justify-center rounded-full bg-status-warning">
              <Text
                variant="caption-strong"
                className="text-center text-content-on-accent"
              >
                {shareCount}
              </Text>
            </Box>
          )}
        </Box>
      </Pressable>
    )}
    <Pressable
      testID={TEST_ID_PLAYER_DETAIL.EDIT_NOTE_BUTTON}
      onPress={onEditPress}
      hitSlop={8}
      className="p-1"
      accessibilityRole="button"
      accessibilityLabel="Edit note"
    >
      <Icon as={Pencil} size="md" className={ICON_CLASS} />
    </Pressable>
    <Pressable
      testID={TEST_ID_PLAYER_DETAIL.REMOVE_NOTE_BUTTON}
      onPress={onDeletePress}
      hitSlop={8}
      className="p-1"
      accessibilityRole="button"
      accessibilityLabel="Delete note"
    >
      <Icon as={Trash2} size="md" className={ICON_CLASS} />
    </Pressable>
  </Stack>
);

NoteActionButtonsView.displayName = "NoteActionButtonsView";

export default memo(NoteActionButtonsView);
